import { getCurrentInstance, reactive, onUnmounted, nextTick } from 'vue';
import { UserInfo, AuthState } from '#/store';
import { Visitor } from '@/config/visitor';
import { useUserStore } from '@/store/modules/user';
import { ElMessageBox } from 'element-plus';
import { TK } from '@/utils/tracker';
import { useI18n } from '@/hooks/web/useI18n';

function useAuth() {
    const loginMap = new Map<Symbol, (state: AuthState) => void>();
    const loginOutMap = new Map<Symbol, () => void>();
    const state = reactive<AuthState>({
        status: 0,
        token: '',
        userInfo: { ...Visitor },
    });

    nextTick(init);

    function init() {
        const userStore = useUserStore();
        state.status = userStore.status;
        state.token = userStore.token;
        state.userInfo = userStore.userInfo;
    }

    function runCallback<T extends (...args: any[]) => any>(
        callback: T,
        ...args: Parameters<T>
    ) {
        if (typeof callback === 'function') {
            callback(...args);
        }
    }

    function onLogin(callback: (state: AuthState) => void) {
        if (state.status === 1) {
            runCallback(callback, state);
        }
        const key = Symbol('onLogin');
        loginMap.set(key, callback);

        // onUnmounted仅能在组建的setup中使用，
        // 通过getCurrentInstance的值是否为null来判断是否在组件实例的setup中调用
        if (getCurrentInstance()) {
            onUnmounted(() => {
                loginMap.delete(key);
            });
        }
    }

    function onLogout(callback: () => void) {
        if (state.status === 2) {
            runCallback(callback);
        }
        const key = Symbol('onLogout');
        loginOutMap.set(key, callback);
        if (getCurrentInstance()) {
            onUnmounted(() => {
                loginOutMap.delete(key);
            });
        }
    }

    function login(userInfo: UserInfo) {
        TK('SYS', 'LOGIN');
        state.status = 1;
        state.token = userInfo.token;
        state.userInfo = userInfo;
        setStore();
        loginMap.forEach((func) => {
            runCallback(func, state);
        });
        window.reload_?.();
    }

    function logout() {
        TK('SYS', 'LOG_OUT');
        const { t } = useI18n();
        ElMessageBox.confirm(t('common.dialog.logoutConfirmContent'), t('common.dialog.logoutConfirm'), {
            confirmButtonText: t('common.confirm'),
            cancelButtonText: t('common.cancel'),
            type: 'warning',
        }).then(() => {
            state.status = 2;
            state.token = '';
            state.userInfo = { ...Visitor };
            setStore();
            loginOutMap.forEach((func) => {
                runCallback(func);
            });

            // ElMessage.success('已退出登录');
        });
    }

    function setStore() {
        const userStore = useUserStore();
        userStore.status = state.status;
        userStore.token = state.token;
        userStore.userInfo = state.userInfo;
    }

    return {
        state,
        login,
        logout,
        onLogin,
        onLogout,
    };
}

export const { state, onLogin, onLogout, login, logout } = useAuth();
